import React, { useState, useContext, useEffect } from 'react'
import useSWR from 'swr'
import { AppContext } from '@state/context'
import { useForm } from 'react-hook-form'
import { Subject_Areas } from '@graphql/queries'
import { closePanel } from '@state/actions/panelsActions'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  BUTTON_TYPE,
  HEADING_TYPE,
  TEXT_SIZES,
  CONTENT_TYPES,
} from '@helpers/constants'
import Divider from '@components/Divider'
import Button from '@components/Button'
import Text from '@components/Text'
import ContentExpander from '@components/ContentExpander'
import Heading from '@components/Heading'
import Form from '@components/Form'
import Icon from '@components/Icon'
import { useUpdateSection } from '@hooks/mutations/useUpdateSection'
import ButtonWithLoader from '@components/ButtonWithLoader'
import SubjectAreaList from '@components/SubjectAreaList'

const ViewSubjectAreaPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const [openId, setOpenId] = useState(null)
  const [isDirty, setIsDirty] = useState(false)
  const [subjectAreasData, setSubjectAreasData] = useState(undefined)

  const {
    app: { editMode, currentSection },
  } = state

  const { post, isPosting } = useUpdateSection()

  let defaultValues =
    currentSection?.subjectAreaTexts?.length > 0
      ? {
          subjectAreaTexts: currentSection.subjectAreaTexts.map(
            (item) => item.id
          ),
        }
      : {
          subjectAreaTexts: [],
        }

  const { register, handleSubmit } = useForm({
    defaultValues: defaultValues,
  })

  const onSubmit = async (data) => {
    const { subjectAreaTexts } = data

    const subjectAreaTextIdsToConnect = subjectAreaTexts.reduce(
      (acc, subjectAreaText) => {
        if (subjectAreaText) {
          const alreadyPresent = currentSection.subjectAreaTexts.find(
            (existingSubjectAreaText) =>
              existingSubjectAreaText.id === subjectAreaText
          )
          if (!alreadyPresent) {
            return [...acc, subjectAreaText]
          } else {
            return acc
          }
        } else {
          return acc
        }
      },
      []
    )

    const subjectAreaTextIdsToDisconnect =
      currentSection.subjectAreaTexts.reduce(
        (acc, previouslyExistingSubjectAreaText) => {
          const stillPresent = subjectAreaTexts.find(
            (subjectAreaText) =>
              subjectAreaText === previouslyExistingSubjectAreaText.id
          )

          if (!stillPresent) {
            return [...acc, previouslyExistingSubjectAreaText.id]
          } else {
            return acc
          }
        },
        []
      )

    ////

    const res = await post(
      {
        id: currentSection.id,
        connectSubjectAreaTexts: subjectAreaTextIdsToConnect,
        disconnectSubjectAreaTexts: subjectAreaTextIdsToDisconnect,
      },
      'Koppling till läroplanen uppdaterad.',
      'Nåt gick fel när koppling till läroplanen skulle uppdateras.'
    )

    if (res.success) {
      setIsDirty(false)
    }
  }

  const { data, error } = useSWR([Subject_Areas, currentSection?.subject?.id])

  useEffect(() => {
    if (data?.subjectAreas?.data) {
      setSubjectAreasData(
        data.subjectAreas.data.map((subjectArea) => ({
          ...subjectArea,
          subjectAreaTexts: subjectArea.subjectAreaTexts.filter(
            (subjectAreaText) => {
              return (
                subjectAreaText.schoolYear.id ===
                  currentSection.schoolYear.id &&
                subjectAreaText.subject.id === currentSection.subject.id
              )
            }
          )
        }))
      )
    }
  }, [data])

  return (
    <>
      <div className={'flex justify-between'}>
        <Text size={TEXT_SIZES.BASE}>Koppling till läroplanen</Text>
        <Button
          handleClick={() => dispatch(closePanel(PANELS.VIEW_SUBJECT_AREA))}
          icon={'Close'}
          size={BUTTON_SIZE.NO_PADDING}
        ></Button>
      </div>
      <Divider size={DIVIDER_SIZE.SMALL} />
      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.LARGE}>
        Centralt innehåll
      </Heading>
      <Divider size={DIVIDER_SIZE.SMALL} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        {!editMode && (
          <SubjectAreaList
            item={currentSection}
            contentType={CONTENT_TYPES.SECTION}
          />
        )}
        {editMode && (
          <>
            <Text>
              Markera de delar av läroplanens centrala innehåll som täcks i ditt
              avsnitt{' '}
            </Text>
            <Divider size={DIVIDER_SIZE.SMALL} />

            {/* <Divider size={DIVIDER_SIZE.SMALL} line /> */}
            {subjectAreasData &&
              subjectAreasData.map((subjectArea, key) => {
                const { name, id } = subjectArea
                return (
                  <div
                    key={`edit-subject-area-${id}`}
                    className={`${
                      key === 0 ? 'border-t' : ''
                    } border-b border-mediumGrey py-3`}
                  >
                    <button
                      className={
                        'flex items-center justify-between w-full focus:outline-none'
                      }
                      type='button'
                      onClick={() => {
                        setOpenId(openId === id ? null : id)
                      }}
                    >
                      <span>{name}</span>
                      <Icon icon={openId === id ? 'ChevronUp' : 'ChevronDown'} />
                    </button>
                    <ContentExpander duration={0.2} open={openId === id}>
                      <Divider size={DIVIDER_SIZE.SMALL} />
                      {subjectArea.subjectAreaTexts.map(
                        (subjectAreaText) => {
                          const { id, text } = subjectAreaText

                          return (
                            <div key={`edit-subject-area-text-${id}`}>
                              <Form.Checkbox
                                id={id}
                                label={text}
                                value={id}
                                onChange={() => !isDirty && setIsDirty(true)}
                                name={'subjectAreaTexts'}
                                ref={register()}
                              />
                            </div>
                          )
                        }
                      )}
                    </ContentExpander>
                  </div>
                )
              })}
            <div className={'pb-4 pt-8'}>
              <ButtonWithLoader
                type={BUTTON_TYPE.PRIMARY}
                isLoading={isPosting}
                disabledText={'Uppdatera'}
                enabledText={'Uppdatera'}
                disabled={isPosting || !isDirty}
              />
            </div>
          </>
        )}
      </Form>
    </>
  )
}

export default ViewSubjectAreaPanel
